'use client';

import { IPointsMallInfo } from '@/api/types/account';
import { CrmPointRule } from '@/api/types/public';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import { useReporting } from '@/hooks/useReporting';
import { ProviderType } from '@/reporting';
import { LocaleType } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { PointFilled, QuestionOutlined, RightArrowOutlined } from '@zeelool/icon';
import { cva } from 'class-variance-authority';
import { useLocale } from 'next-intl';
import { useTranslations } from '@/i18n/use-translations';
import React, { useContext, useState } from 'react';
import RedeemedHistory from './RedeemedHistory';
interface IPromoBannerProps {
  freeData: IPointsMallInfo;
  crmPointsRules: CrmPointRule;
  onTipsClick?: () => void;
}

const PromoBanner: React.FC<IPromoBannerProps> = ({ freeData, crmPointsRules, onTipsClick }) => {
  const { deviceType } = useContext(ResponsiveContext);
  const [showRedeemedHistory, setShowRedeemedHistory] = useState<boolean>(false);
  const t = useTranslations('account.pointsMall.promoBanner');
  const { trackEvent } = useReporting();
  const locale = useLocale() as LocaleType;
  const webHistoryTextFontSize = cva('', {
    variants: {
      locale: {
        en: 'text-14',
        fr: 'text-12',
        de: 'text-14',
        es: 'text-14',
        it: 'text-14',
        pt: 'text-14',
        fi: 'text-14',
        nl: 'text-14',
      },
    },
  });
  const mobileHistoryTextFontSize = cva('', {
    variants: {
      locale: {
        en: 'text-12',
        fr: 'text-10',
        de: 'text-12',
        es: 'text-12',
        it: 'text-12',
        pt: 'text-12',
        fi: 'text-12',
        nl: 'text-12',
      },
    },
  });
  if (!freeData) return null;
  const PointsMallPageBtn_Click = (btnName: string) => {
    trackEvent(
      {
        eventName: 'PointsMallPageBtn_Click',
        properties: {
          btn_name: btnName,
        },
      },
      [ProviderType.SENSORS],
    );
  };
  return (
    <>
      <Box
        sx={{
          background: '#CCFF67',
          borderRadius: '12px',
          position: 'relative',
          padding: deviceType === EPlatform.WEB ? '24px 0 20px 24px' : '20px 0 16px 20px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        {/* 数字和统计信息 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div className={`flex ${deviceType === EPlatform.WEB ? 'items-center' : 'items-end'}`}>
            <span
              className={`${deviceType === EPlatform.WEB ? 'text-[40px] leading-[48px]' : 'text-[36px] leading-[43px]'} font-bb`}
            >
              {freeData.points}
            </span>
            <span
              className={`${deviceType === EPlatform.WEB ? 'text-[16px] leading-[19px]' : 'text-[14px] leading-[17px]  translate-y-[-6px]'} ml-8`}
            >
              {freeData.points > 1 || freeData.points === 0 ? t('points_plural') : t('points')}
            </span>
            <WapOnly>
              <QuestionOutlined
                className="text-12 ml-[5px]  translate-y-[-8px]"
                onClick={() => {
                  onTipsClick();
                  PointsMallPageBtn_Click(t('learnAboutPoints'));
                }}
              />
            </WapOnly>
          </div>
          <div
            className={`relative z-2 mt-28 text-[12px] leading-[14px] ${
              deviceType === EPlatform.WEB ? ' pr-[76px]' : ' pr-[68px]'
            }`}
          >
            {Number(freeData.point_set) > 1
              ? t('orderExchangeRate_plural', {
                  points: freeData.point_set,
                })
              : t('orderExchangeRate', {
                  points: freeData.point_set,
                })}
          </div>
          <div
            className={`relative z-2 text-[12px] leading-[14px] ${deviceType === EPlatform.WEB ? 'mt-8  pr-[76px]' : 'mt-6  pr-[68px]'}`}
          >
            {Number(crmPointsRules.deduct?.integral_product_ratio) > 1
              ? t('rewardsExchangeRate_plural', {
                  points: crmPointsRules.deduct?.integral_product_ratio,
                })
              : t('rewardsExchangeRate_plural', {
                  points: crmPointsRules.deduct?.integral_product_ratio,
                })}
          </div>
        </Box>

        {/* 历史按钮 - 右上角 */}
        <div
          onClick={() => {
            setShowRedeemedHistory(true);
            PointsMallPageBtn_Click('History');
          }}
          className={`absolute top-0 right-0 bg-[url('/zeelool-next/account/redeemable-items/history_bg.png')] bg-no-repeat bg-contain text-white text-right  box-border cursor-pointer ${
            deviceType === EPlatform.WEB
              ? `w-[110px] h-[43px] ${webHistoryTextFontSize({ locale })} pr-8 pt-6`
              : `w-[89px] h-[35px] ${mobileHistoryTextFontSize({ locale })} pr-7 pt-5`
          }`}
        >
          {t('history')}{' '}
          <RightArrowOutlined
            className={`inline-block ${deviceType === EPlatform.WEB ? 'text-14 leading-[17px]' : 'text-12 leading-[14px]'}`}
          />
        </div>

        {/* 右侧票券图标 */}
        <Box
          sx={{
            position: 'absolute',
            right: '20px',
            bottom: '16px',
            zIndex: 1,
          }}
        >
          <PointFilled
            className={`${deviceType === EPlatform.WEB ? 'text-[56px]' : 'text-[48px]'}`}
          />
        </Box>
      </Box>
      <RedeemedHistory show={showRedeemedHistory} onClose={() => setShowRedeemedHistory(false)} />
    </>
  );
};

export default PromoBanner;
